/** @format */

import { useEffect } from 'react';
import styled from 'styled-components';
import { useAnimation, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { breakpointFrom } from '../../theme/mixins';

const Title = styled.h2`
  padding-bottom: 0 !important;
  font-size: 3rem;
  font-weight: 600;
`;

const Word = styled(motion.span)`
  display: inline-block;
  margin-right: 0.25em;
  white-space: nowrap;
`;

const Character = styled(motion.span)`
  display: inline-block;
  margin-right: -0.05em;
  font-family: ${({ theme }) => theme.font.accent};
  font-size: clamp(4.75rem, 2.5rem + 10vw, 12.5rem);
  color: ${({ theme }) => theme.color.yellow};
  line-height: clamp(5rem, 1.6667rem + 9.2593vw, 30rem);

  ${breakpointFrom.xl`
  line-height: 12rem;
  `}
  letter-spacing: clamp(0.03125rem, -0.0071rem + 0.1704vw, 0.14625rem);
  font-weight: 600;
  z-index: 3;
`;

export const AnimatedTitle = ({ text }: { text: string }) => {
  const ctrls = useAnimation();

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      ctrls.start('visible');
    }
    if (!inView) {
      ctrls.start('hidden');
    }
  }, [ctrls, inView]);

  const wordAnimation = {
    hidden: {},
    visible: {},
  };

  const characterAnimation = {
    hidden: {
      opacity: 0,
      y: `0.25em`,
    },
    visible: {
      opacity: 1,
      y: `0em`,
      transition: {
        duration: 1,
        ease: [0.2, 0.65, 0.3, 0.9],
      },
    },
  };

  return (
    <Title aria-label={text} role="heading">
      {text.split(' ').map((word, index) => {
        return (
          <Word
            ref={ref}
            aria-hidden="true"
            key={index}
            initial="hidden"
            animate={ctrls}
            variants={wordAnimation}
            transition={{
              delayChildren: index * 0.25,
              staggerChildren: 0.05,
            }}
          >
            {word.split('').map((character, index) => {
              return (
                <Character aria-hidden="true" key={index} variants={characterAnimation}>
                  {character}
                </Character>
              );
            })}
          </Word>
        );
      })}
    </Title>
  );
};
